import 'lite-youtube-embed'
import 'lite-vimeo-embed/module/lite-vimeo-embed'
import 'virtual:svg-icons-register'

import Swup from 'swup'
import SwupHeadPlugin from '@swup/head-plugin'
import SwupA11yPlugin from '@swup/a11y-plugin'
import SwupDebugPlugin from '@swup/debug-plugin'
import SwupProgressPlugin from '@swup/progress-plugin'

import { createPinia } from 'pinia'
import { useUiStore } from '~/store/modules/ui'

import { SentryConfig } from '~/types/SentryConfig'
import { AccountEnv } from '~/types/AccountEnv'

import Hydrater from '~/hydrater/Hydrater'
import HydrationStrategy from '~/hydrater/base/HydrationStrategy'
import VueHydratable from '~/hydrater/hydratables/VueHydratable'

import { useApp } from '~/composables/useApp'
import { Formie, handleFormieLoad, initFormieForms, destroyFormieForms } from '~/composables/useFormie'

import.meta.glob('../img/**/*', { query: '?url', import: 'default', eager: true })

// Config from HTML
const COMPREHENSIBLE_CLASSROOM = window.COMPREHENSIBLE_CLASSROOM || {
  formieLoaded() {
    this.CONFIG.FORMIE_LOADED = true;
  },
  CONFIG: {
    DEBUG: false,
    FORMIE_LOADED: false,
    SENTRY: {} as SentryConfig,
    URLS: {} as Record<string, string>,
    ACCOUNT_ENV: {} as AccountEnv,
  },
}
if (COMPREHENSIBLE_CLASSROOM.CONFIG.FORMIE_LOADED) {
  // continue immediately
  handleFormieLoad(window.Formie)
} else {
  // replace the script load handler
  COMPREHENSIBLE_CLASSROOM.formieLoaded = () => {
    COMPREHENSIBLE_CLASSROOM.CONFIG.FORMIE_LOADED = true
    handleFormieLoad(window.Formie)
  }
}


// Globally shared pinia instance for all Vue apps
const pinia = createPinia()
// We need to initialize a Vue instance with Pinia for useUiStore to work in our Swup
// hook below. We never do anything else with this component, it's just a dummy / kludge
useApp(pinia, {
  mountName: 'StoreManager',
})
const uiStore = useUiStore()

// @see https://swup.js.org/plugins/
const swupPlugins = [
  new SwupProgressPlugin(),
  new SwupHeadPlugin(),
  new SwupA11yPlugin(),
]

if (COMPREHENSIBLE_CLASSROOM.CONFIG?.DEBUG) {
  swupPlugins.push(new SwupDebugPlugin())
}

// Page transition manager
const swup = new Swup({
  containers: ['#main', '#store-config'],
  // animationScope: 'containers',
  plugins: swupPlugins,
})

// Component Hydrater - see src/assets/js/hydrater/README.md for more info
const hydrater = new Hydrater([
  new HydrationStrategy({
    name: 'vue',
    hydratable: VueHydratable,
    factory: (el, { mountName }) => useApp(pinia, {
      mountName: `Hydrated${mountName ||'App'}`,
      ...COMPREHENSIBLE_CLASSROOM,
      FLAGS: {
        withSentry: true,
      },
    }),
  }),
])

// Don't mount components if we have Craft / Larapack debug dump output in the page
const shouldMountComponents = !import.meta.env.DEV || typeof window?.Sfdump !== 'function'

if (shouldMountComponents) {
  document.querySelector('html').classList.add('js-loaded')
  // Mount components on page load
  window.addEventListener('DOMContentLoaded', () => {
    hydrater.update()
    // Remove noscript lite-youtube iframes after initial hydrater update (autoplays during Vite HMR and Swup transitions)
    document.querySelectorAll('lite-youtube noscript').forEach(noscript => {
      noscript.remove()
    })
    initFormieForms()
  })

  // Clean up/reset Vue state when we start a transition
  swup.hooks.on('animation:out:start', () => {
    uiStore.startPageTransition()
    uiStore.resetMobileNav()
    uiStore.toggleNavDropdown()
  })
  swup.hooks.on('animation:in:start', () => {
    uiStore.endPageTransition()
  })

  // Tear down Formie forms (and their body-level scripts) before the old content is swapped out,
  // so page:view can re-initialize them from scratch (fixes Turnstile on back/forward navigation)
  swup.hooks.before('content:replace', async () => {
    await destroyFormieForms()
  })

  swup.hooks.on('page:view', (visit) => {
    hydrater.update()
    // Remove noscript lite-youtube iframes after initial hydrater update (autoplays during Vite HMR and Swup transitions)
    document.querySelectorAll('lite-youtube noscript').forEach(noscript => {
      noscript.remove()
    })
    uiStore.setRoute()
    initFormieForms()
  })
}
// Expose our global state to the window object to aid debugging
window.COMPREHENSIBLE_CLASSROOM = {
  ...COMPREHENSIBLE_CLASSROOM,
  swup,
  hydrater,
  pinia,
  formie: Formie,
}
